import Papa from "papaparse";
import * as XLSX from "xlsx";

/**
 * File parsing utilities for CSV and Excel files.
 * Returns raw rows as string[][] for further processing.
 */

const formatDateCell = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cellToString = (c: any): string => {
  if (c == null) return "";
  if (c instanceof Date) return formatDateCell(c);

  return String(c);
};

/**
 * Parse a CSV file into rows of strings
 */
export const parseCsvFileToRows = async (file: File): Promise<string[][]> => {
  return new Promise<string[][]>((resolve) => {
    Papa.parse(file as unknown as File, {
      header: false,
      skipEmptyLines: "greedy",
      delimiter: "",
      complete: (results) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rows = (results.data as unknown as any[][]).map((r) =>
          Array.isArray(r) ? r.map((c) => String(c ?? "")) : [],
        );

        resolve(rows);
      },
      error: () => {
        resolve([]);
      },
    });
  });
};

/**
 * Parse an Excel file into rows of strings
 */
export const parseExcelFileToRows = async (file: File): Promise<string[][]> => {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: "array", cellDates: true });
  const firstSheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[firstSheetName];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowsAny: any[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: "",
  });

  return rowsAny.map((row) => row.map(cellToString));
};

/**
 * Parse a file (CSV or Excel) into rows of strings based on file extension
 */
export const parseFileToRows = async (file: File): Promise<string[][]> => {
  const name = (file.name || "").toLowerCase();
  const isExcel = name.endsWith(".xlsx") || name.endsWith(".xls");

  return isExcel ? parseExcelFileToRows(file) : parseCsvFileToRows(file);
};

/**
 * Check if a file is an Excel file based on its extension
 */
export const isExcelFile = (file: File): boolean => {
  const name = (file.name || "").toLowerCase();


return name.endsWith(".xlsx") || name.endsWith(".xls");
};
